import styled, { css } from "styled-components";

import { colors, fonts, fontSizes } from "@/styles/design-system";

/**
 * The small plan pills — blue "Premium" and green "Free" — used in the pricing,
 * subscription and YouTube-licence boxes and their modals.
 *
 * ONE base, two colours. Both were previously defined in more than one file,
 * with the same intent and small differences that showed: Free sat a pixel taller
 * than Premium because it lacked the line-height reset. Sharing the base is what
 * makes "the same size" true by construction rather than by everyone remembering.
 *
 * **No icon inside.** Premium carried a gold diamond, which fought the pill: it is
 * blue precisely so a plan state does not wear the gold that means "press me" on
 * this site, and a gold glyph put that back.
 *
 * `$nudge` shifts the pill vertically. It sits inline in text of several sizes
 * and has to land on each baseline; there is no single correct value.
 *
 * `$spaced` adds side margins. Opt-in, because the pricing boxes are already
 * spaced correctly and adding it everywhere moved something that was right.
 */
const base = css<{ $nudge?: number; $spaced?: boolean }>`
  /* inline-BLOCK, not inline-flex. inline-flex was only needed to centre the
     icon that used to live inside; with text alone it buys nothing, and it
     interacts badly with the hanging indents these pills sit inside. */
  display: inline-block;

  /* 🔴 THE ONE THAT BITES: text-indent is INHERITED, and an inline-block is a
     block container — so it re-applies its parent's indent to its OWN text. The
     licence modal's feature rows use a hanging indent
     (padding-left: 18px; text-indent: -18px), which dragged the word inside the
     pill 18px left, out of its own background and on top of the word before it.
     Reset here, not per call site: any row with a hanging indent does the same.
     inline-flex hid it, because flex items ignore text-indent — which is why it
     only appeared once the pill no longer needed to be a flex container. */
  text-indent: 0;

  /* Stops the pill inflating the line box around it — those rows set their own
     line-height (1.6 in the modal) and a taller inline box spreads the whole
     row. This is also what keeps both pills the same height. */
  line-height: 1;

  padding: 2px 7px 3px;
  /* Side spacing is OPT-IN, never a default. The pricing boxes already sit
     right and a blanket margin moved them; only the licence modal's rows, which
     are tighter, need the extra air. */
  margin: ${({ $spaced }) => ($spaced ? "0 4px" : "0")};
  border-radius: 8px;
  font-family: ${fonts.number};
  font-size: ${fontSizes.xs};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  position: relative;
  top: ${({ $nudge = 0 }) => `${$nudge}px`};
`;

export const PremiumPill = styled.span<{ $nudge?: number; $spaced?: boolean }>`
  ${base};
  background: ${colors.tier.premium.bg};
  color: ${colors.tier.premium.text};
`;

export const FreePill = styled.span<{ $nudge?: number; $spaced?: boolean }>`
  ${base};
  background: ${colors.tier.free.bg};
  color: ${colors.tier.free.text};
`;
